import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import PackageStore from './PackageStore';
import { toJS } from 'mobx';
import OverlayContainer from '../../Components/UI/LoadingOverlay/OverlayContainer';
import { PackageDetails } from '../../SubModules/PackageDetails/PackageDetails';
import { PackageBestPriceDetailsProps } from '../../SubModules/PackageDetails/PackageBestPriceDetails';
import Overlay from '../../Components/UI/LoadingOverlay/Overlay';
import Props from './externals';
import BestPriceAvailabilityStore from '../BestPriceAvailability/BestPriceAvailabilityStore';
import {
  CollapsibleRef,
  IsStickyListenerType
} from '../../SubModules/SearchCollapsible/SearchCollapsible';
import BestPriceAvailabilitySearchStore from '../../SubModules/BestPriceAvailabilitySearch/BestPriceAvailabilitySearchStore';
import BestPrice from './BestPrice';
import { ApiItemType } from '@ibe/api';
import PackageParams from '../../SubModules/PackageDetails/PackageParams';

type ChangeType = 'duration' | 'Date' | 'participants' | 'origins';

const Details = observer(
  (props: {
    store: PackageStore;
    showTotalPriceOnly?: boolean;
    onSelect: Props['onSelect'];
    goBackToList: () => void;
    bestPriceMaxDateRangeSpan?: number;
    showLabels?: boolean;
    destinationMandatory?: boolean;
    endDateMandatory?: boolean;
    showMealDetails?: boolean;
    availabilitySortingActive?: boolean;
    onBestPriceAvailabilitySelect?: (packageCartId?: string) => void;
    minSelectableChildAge?: number;
    noDatePickerOverflow?: boolean;
    showCharacteristics?: boolean;
    changeOnBlur?: boolean;
    onlyAdultSelect?: boolean;
    bestPriceStickySearch?: boolean;
    bestPriceRenderSearchContent?: (
      store: BestPriceAvailabilityStore,
      collapsibleRef?: CollapsibleRef
    ) => ReactNode;
    bestPriceDesktopSearchCollapse?: boolean;
    bestPriceIsStickyListener?: IsStickyListenerType;
    onBestPriceSearchChange?: (a: {
      bestPriceStore?: BestPriceAvailabilityStore;
      bestPriceSearchStore?: BestPriceAvailabilitySearchStore;
      packageStore?: PackageStore;
      changeType: ChangeType;
    }) => void;
    bestPriceStickyOffset?: number;
    bestPriceSubmitOnChange?: boolean;
    widgetClickable?: boolean;
    bestPriceHideDetailBackButton?: boolean;
    ExternalBestPriceDetailsContent?: PackageBestPriceDetailsProps['ExternalBestPriceDetailsContent'];
    bestPriceListSelectionsMode?: boolean;
    includeCheapestFlightInRoomPrice?: boolean;
    splitRequiredComponentPricePerPerson?: boolean;
    subType?: ApiItemType;
    withRoomCountSelector?: boolean;
    bestPriceAfterSearch?: (params: PackageParams) => void;
    bestPriceShowDirectFlightsOnlyFilter?: boolean;
    internalFlightLogo?: string;
    showInternalFlightLogo?: boolean;
    scrollOnInitialSearch?: boolean;
    useGeoUnitTwoColumnStyling?: boolean;
  }) => {
    const {
      store,
      showTotalPriceOnly,
      onSelect,
      goBackToList,
      bestPriceMaxDateRangeSpan,
      showLabels,
      destinationMandatory,
      endDateMandatory,
      availabilitySortingActive,
      onBestPriceAvailabilitySelect,
      showMealDetails,
      minSelectableChildAge,
      noDatePickerOverflow,
      showCharacteristics,
      changeOnBlur,
      onlyAdultSelect,
      bestPriceStickySearch,
      bestPriceRenderSearchContent,
      bestPriceDesktopSearchCollapse,
      bestPriceIsStickyListener,
      onBestPriceSearchChange,
      bestPriceStickyOffset,
      bestPriceSubmitOnChange,
      widgetClickable,
      bestPriceHideDetailBackButton,
      ExternalBestPriceDetailsContent,
      bestPriceListSelectionsMode,
      subType,
      withRoomCountSelector,
      includeCheapestFlightInRoomPrice,
      splitRequiredComponentPricePerPerson,
      bestPriceAfterSearch,
      bestPriceShowDirectFlightsOnlyFilter,
      internalFlightLogo,
      showInternalFlightLogo,
      scrollOnInitialSearch,
      useGeoUnitTwoColumnStyling
    } = props;

    return (
      <OverlayContainer>
        {store.isPackageModel(store.detail) && (
          <PackageDetails
            packageDetailId={toJS(store.searchParams).packageDetailId || ''}
            travelDateId={toJS(store.searchParams).travelDateId || ''}
            item={toJS(store.detail)}
            onSelect={(s): void => {
              if (onSelect) {
                onSelect(s);
              }
            }}
            showTotalPriceOnly={showTotalPriceOnly}
          />
        )}
        {store.isBestPricePackageModel(store.detail) && (
          <BestPrice
            item={store.detail}
            goBackToList={goBackToList}
            showMealDetails={showMealDetails}
            showCharacteristics={showCharacteristics}
            hideDetailBackButton={bestPriceHideDetailBackButton}
            onSelect={(s): void => {
              if (onSelect) {
                onSelect(s);
              }
            }}
            ExternalBestPriceDetailsContent={ExternalBestPriceDetailsContent}
            bestPriceMaxDateRangeSpan={bestPriceMaxDateRangeSpan}
            showLabels={showLabels}
            withDateRangePicker
            destinationMandatory={destinationMandatory}
            endDateMandatory={endDateMandatory}
            packageStore={store}
            availabilitySortingActive={availabilitySortingActive}
            onBestPriceAvailabilitySelect={onBestPriceAvailabilitySelect}
            minSelectableChildAge={minSelectableChildAge}
            noDatePickerOverflow={noDatePickerOverflow}
            changeOnBlur={changeOnBlur}
            onlyAdultSelect={onlyAdultSelect}
            bestPriceStickySearch={bestPriceStickySearch}
            bestPriceRenderSearchContent={bestPriceRenderSearchContent}
            bestPriceDesktopSearchCollapse={bestPriceDesktopSearchCollapse}
            bestPriceIsStickyListener={bestPriceIsStickyListener}
            widgetClickable={widgetClickable}
            onBestPriceSearchChange={a =>
              onBestPriceSearchChange &&
              onBestPriceSearchChange({
                bestPriceSearchStore: a.bestPriceSearchStore,
                bestPriceStore: a.bestPriceStore,
                packageStore: store,
                changeType: a.changeType
              })
            }
            bestPriceStickyOffset={bestPriceStickyOffset}
            bestPriceSubmitOnChange={bestPriceSubmitOnChange}
            bestPriceListSelectionsMode={bestPriceListSelectionsMode}
            store={store}
            subType={subType}
            includeCheapestFlightInRoomPrice={includeCheapestFlightInRoomPrice}
            splitRequiredComponentPricePerPerson={splitRequiredComponentPricePerPerson}
            withRoomCountSelector={withRoomCountSelector}
            afterSearch={bestPriceAfterSearch}
            bestPriceShowDirectFlightsOnlyFilter={bestPriceShowDirectFlightsOnlyFilter}
            internalFlightLogo={internalFlightLogo}
            showInternalFlightLogo={showInternalFlightLogo}
            scrollOnInitialSearch={scrollOnInitialSearch}
            useGeoUnitTwoColumnStyling={useGeoUnitTwoColumnStyling}
          />
        )}
        {store.isLoading && <Overlay />}
      </OverlayContainer>
    );
  }
);

export default Details;
